/* ==========================================================================
   Predictive search.

   Results come from Shopify's built-in /search/suggest.json — the search
   engine, ranking and query suggestions are all platform features. This file
   is only the UI: debounce, fetch, render, keyboard handling.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('predictive-search');
  if (!root) return;

  var input = root.querySelector('[data-predictive-input]');
  var panel = root.querySelector('[data-predictive-panel]');
  var results = root.querySelector('[data-predictive-results]');
  var reset = root.querySelector('[data-predictive-reset]');
  if (!input || !panel || !results) return;

  var suggestUrl = root.dataset.suggestUrl;
  var limit = Number(root.dataset.limit) || 6;
  var strings = (window.Voltage && window.Voltage.strings) || {};
  var moneyFormat = (window.Voltage && window.Voltage.moneyFormat) || '${{amount}}';

  var MIN_CHARS = 2;
  var DEBOUNCE = 250;

  var controller = null;
  var debounceTimer = null;
  var activeIndex = -1;

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // suggest.json returns prices as plain decimals ("998.00"), so only the
  // shop's money format needs applying here.
  function money(value) {
    var n = Number(value);
    if (isNaN(n)) return '';
    return String(moneyFormat)
      .replace(/\{\{\s*amount\s*\}\}/g, n.toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(n)))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(n)).replace('.', ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, n.toFixed(2).replace('.', ','));
  }

  function open() {
    if (panel.hasAttribute('hidden')) panel.removeAttribute('hidden');
    input.setAttribute('aria-expanded', 'true');
  }

  function close() {
    panel.setAttribute('hidden', '');
    input.setAttribute('aria-expanded', 'false');
    activeIndex = -1;
  }

  function imageMarkup(item) {
    var src = item.image || (item.featured_image && item.featured_image.url);
    if (!src) return '<span class="predictive-result__media predictive-result__media--empty"></span>';
    var alt = (item.featured_image && item.featured_image.alt) || item.title || '';
    return '<span class="predictive-result__media"><img src="' + esc(src) + '" alt="' + esc(alt) + '" loading="lazy" width="56" height="56"></span>';
  }

  function rowMarkup(item, kind) {
    var title = item.title || item.text || '';
    var subtitle = item.vendor || item.type || '';
    if (kind === 'products' && item.price) subtitle = money(item.price);
    return (
      '<a class="predictive-result predictive-result--' + kind + '" href="' + esc(item.url) + '" role="option">' +
      (kind === 'products' || kind === 'collections' || kind === 'articles' ? imageMarkup(item) : '') +
      '<span class="predictive-result__body">' +
      (item.styled_text
        ? '<span class="predictive-result__title">' + item.styled_text + '</span>'
        : '<span class="predictive-result__title">' + esc(title) + '</span>') +
      (subtitle ? '<span class="predictive-result__sub-title">' + esc(subtitle) + '</span>' : '') +
      '</span></a>'
    );
  }

  function sectionMarkup(label, items, kind) {
    if (!items.length) return '';
    return (
      '<div class="predictive-search__group" data-predictive-group="' + kind + '" hidden>' +
      '<p class="predictive-search__heading">' + esc(label) + '</p>' +
      items.map(function (item) { return rowMarkup(item, kind); }).join('') +
      '</div>'
    );
  }

  function render(data) {
    var r = (data.resources && data.resources.results) || {};
    var queries = r.queries || [];
    var products = r.products || [];
    var collections = r.collections || [];
    var articles = r.articles || [];
    var pages = r.pages || [];

    if (!queries.length && !products.length && !collections.length && !articles.length && !pages.length) {
      results.innerHTML =
        '<p class="predictive-search__empty">' +
        esc((strings.noResults || 'No results for “{{ terms }}”').replace('{{ terms }}', input.value)) +
        '</p>';
      open();
      return;
    }

    var html = '';

    // Suggestions sit above the tabs, on their own tinted band.
    if (queries.length) {
      html += '<div class="predictive-search__suggestions">' +
        '<p class="predictive-search__heading">' + esc(strings.suggestions || 'Suggestions') + '</p>' +
        queries.map(function (q) { return rowMarkup(q, 'queries'); }).join('') +
        '</div>';
    }

    var tabs = [];
    if (products.length) tabs.push({ kind: 'products', label: strings.products || 'Products' });
    if (collections.length) tabs.push({ kind: 'collections', label: strings.collections || 'Collections' });
    if (articles.length) tabs.push({ kind: 'articles', label: strings.articles || 'Articles' });
    if (pages.length) tabs.push({ kind: 'pages', label: strings.pages || 'Pages' });

    if (tabs.length) {
      html += '<div class="predictive-search__tabs" role="tablist">' +
        tabs.map(function (t, i) {
          return '<button type="button" role="tab" class="predictive-search__tab" data-predictive-tab="' + t.kind + '"' +
            ' aria-selected="' + (i === 0 ? 'true' : 'false') + '">' + esc(t.label) + '</button>';
        }).join('') + '</div>';

      html += sectionMarkup('', products, 'products');
      html += sectionMarkup('', collections, 'collections');
      html += sectionMarkup('', articles, 'articles');
      html += sectionMarkup('', pages, 'pages');

      html += '<a class="predictive-search__view-all" href="' + esc(root.dataset.searchUrl || '/search') +
        '?q=' + encodeURIComponent(input.value) + '&type=product">' +
        esc(strings.viewAll || 'View all results') +
        '<span class="predictive-search__view-all-icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
        '</span></a>';
    }

    results.innerHTML = html;

    // Show the first tab's group; groups are hidden by default so nothing flashes.
    var first = tabs.length ? tabs[0].kind : null;
    if (first) showTab(first);

    results.querySelectorAll('[data-predictive-tab]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showTab(btn.dataset.predictiveTab);
        input.focus();
      });
    });

    open();
  }

  function showTab(kind) {
    results.querySelectorAll('[data-predictive-group]').forEach(function (g) {
      if (g.dataset.predictiveGroup === kind) g.removeAttribute('hidden');
      else g.setAttribute('hidden', '');
    });
    results.querySelectorAll('[data-predictive-tab]').forEach(function (b) {
      b.setAttribute('aria-selected', b.dataset.predictiveTab === kind ? 'true' : 'false');
    });
    activeIndex = -1;
  }

  function visibleOptions() {
    var group = results.querySelector('[data-predictive-group]:not([hidden])');
    var scope = group || results;
    return Array.prototype.slice.call(scope.querySelectorAll('[role="option"]'));
  }

  function setActive(options, next) {
    if (!options.length) return;
    options.forEach(function (o) { o.removeAttribute('aria-selected'); });
    activeIndex = (next + options.length) % options.length;
    var el = options[activeIndex];
    el.setAttribute('aria-selected', 'true');
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }

  function search(term) {
    if (controller) controller.abort();
    controller = new AbortController();

    var params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,article,page,query',
      'resources[limit]': String(limit),
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
    });

    fetch(suggestUrl + '?' + params.toString(), {
      signal: controller.signal,
      headers: { Accept: 'application/json' }
    })
      .then(function (res) { return res.json(); })
      .then(render)
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
      });
  }

  input.addEventListener('input', function () {
    var term = input.value.trim();
    if (reset) reset.hidden = term.length === 0;

    clearTimeout(debounceTimer);
    if (term.length < MIN_CHARS) {
      close();
      return;
    }
    debounceTimer = setTimeout(function () { search(term); }, DEBOUNCE);
  });

  input.addEventListener('focus', function () {
    if (input.value.trim().length >= MIN_CHARS && results.innerHTML) open();
  });

  input.addEventListener('keydown', function (event) {
    var options = visibleOptions();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(options, activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(options, activeIndex - 1);
    } else if (event.key === 'Enter' && activeIndex > -1 && options[activeIndex]) {
      event.preventDefault();
      options[activeIndex].click();
    } else if (event.key === 'Escape') {
      close();
    }
  });

  if (reset) {
    reset.addEventListener('click', function () {
      input.value = '';
      reset.hidden = true;
      close();
      input.focus();
    });
  }

  document.addEventListener('click', function (event) {
    if (!root.contains(event.target)) close();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') close();
  });

  // A product-type choice is part of the query, so re-run it.
  var typeSelect = root.querySelector('.header__search-types-select');
  if (typeSelect) {
    typeSelect.addEventListener('change', function () {
      if (input.value.trim().length >= MIN_CHARS) search(input.value.trim());
    });
  }
})();
